import React, { Fragment, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack, MailOutline } from '@mui/icons-material';
import { getApiErrorMessage, useApiGet } from '../../utils/httpClientTs';
import {
  getFinanceElfLoanDetail,
  patchFinanceElfLoanRecurringAmount,
} from '../../endpoints/financeElfLoans';
import {
  getLoanCatchUpQuote,
  getLoanPayoffQuote,
  setLoanPrincipalOnly,
  type LoanCatchUpQuote,
  type LoanPayoffQuote,
} from '../../endpoints/loans';
import { formatCalendarDate } from '../../utils/formatCalendarDate';
import type {
  ElfLoanDetailLoan,
  ElfLoanDetailTransactionEntry,
  ElfLoanDetailTransactionLine,
  FinanceElfLoanDetailResponse,
} from '../../types/financeElfLoans';
import ElfLoanAccountOnFileDialog from './ElfLoanAccountOnFileDialog';
import ElfLoanPaymentDialog from './ElfLoanPaymentDialog';
import {
  AddChargeDialog,
  CorrectCheckPaymentDialog,
  RemoveChargeDialog,
  UnmatchPayPalPaymentDialog,
} from './ElfLoanPaymentCorrectionDialogs';

const HISTORY_PAGE_SIZE_DEFAULT = 10;

const todayISODate = (): string => new Date().toISOString().slice(0, 10);

const formatMoney = (value: number): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatInterestRate = (rate: number): string => {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '—';
  return `${rate.toFixed(2)}%`;
};

/** Payflow EXPDATE is MMYY; display as MM/YY. */
const formatRecurringExpDate = (exp?: string | null): string => {
  const s = (exp || '').trim();
  if (!s) return '—';
  if (s.length === 4 && /^\d{4}$/.test(s)) {
    return `${s.slice(0, 2)}/${s.slice(2)}`;
  }
  return s;
};

const formatBalanceValue = (value: number): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  check: 'Check',
  stripe: 'Stripe',
  on_file: 'On file',
  paypal: 'PayPal',
  ach: 'ACH',
};

const formatPaymentEventLabel = (
  entry: ElfLoanDetailTransactionEntry
): string => {
  if (entry.event_type !== 'Payment') {
    return entry.event_type;
  }
  const method = entry.payment_method
    ? (PAYMENT_METHOD_LABELS[entry.payment_method] ?? entry.payment_method)
    : null;
  const ref = entry.payment_ref?.trim();
  if (method && ref) {
    return `Payment (${method} #${ref})`;
  }
  if (method) {
    return `Payment (${method})`;
  }
  return 'Payment';
};

const appliedByBucket = (
  entry: ElfLoanDetailTransactionEntry
): { interest: number; principal: number; fees: number } => {
  let interest = 0;
  let principal = 0;
  let fees = 0;
  for (const line of entry.lines ?? []) {
    const applied = Math.abs(line.amount);
    switch (line.bucket) {
      case 'interest':
        interest += applied;
        break;
      case 'principal':
        principal += applied;
        break;
      case 'charges':
        fees += applied;
        break;
      default:
        break;
    }
  }
  return { interest, principal, fees };
};

const formatApplied = (value: number): string => {
  if (!value) return '—';
  return formatBalanceValue(value);
};

const balanceChangeLinesFromLine = (
  line: ElfLoanDetailTransactionLine
): string[] => {
  const lines: string[] = [];
  if (line.principal_balance !== line.new_principal_balance) {
    lines.push(
      `Principal: ${formatBalanceValue(line.principal_balance)} → ${formatBalanceValue(line.new_principal_balance)}`
    );
  }
  if (line.interest_balance !== line.new_interest_balance) {
    lines.push(
      `Interest: ${formatBalanceValue(line.interest_balance)} → ${formatBalanceValue(line.new_interest_balance)}`
    );
  }
  if (line.charges_balance !== line.new_charges_balance) {
    lines.push(
      `Fees: ${formatBalanceValue(line.charges_balance)} → ${formatBalanceValue(line.new_charges_balance)}`
    );
  }
  return lines;
};

const transactionExpandLines = (
  entry: ElfLoanDetailTransactionEntry
): string[] => {
  const out: string[] = [];
  if (entry.event_type === 'Charge' && entry.charge_name) {
    out.push(entry.charge_name);
  }
  for (const line of entry.lines ?? []) {
    out.push(...balanceChangeLinesFromLine(line));
  }
  return out;
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      py: 0.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, minWidth: 140 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{ textAlign: 'right', wordBreak: 'break-word' }}
    >
      {value}
    </Typography>
  </Box>
);

const SectionCard: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}> = ({ title, action, children, sx }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderColor: 'divider',
      boxShadow: 'none',
      ...sx,
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </CardContent>
  </Card>
);

const formatCosignerAddress = (c: {
  address_1?: string;
  address_2?: string;
  city?: string;
  postal_code?: string;
}): string => {
  const line1 = [c.address_1, c.address_2].filter(Boolean).join(', ');
  const line2 = [c.city, c.postal_code].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(' · ');
};

const ElfLoanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedEntryIds, setExpandedEntryIds] = useState<string[]>([]);
  const [cosignerLoan, setCosignerLoan] = useState<ElfLoanDetailLoan | null>(
    null
  );
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [addChargeOpen, setAddChargeOpen] = useState(false);
  const [accountOnFileOpen, setAccountOnFileOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState<string | null>(null);
  const [recurringAmountOpen, setRecurringAmountOpen] = useState(false);
  const [recurringAmountInput, setRecurringAmountInput] = useState('');
  const [recurringAmountBusy, setRecurringAmountBusy] = useState(false);
  const [recurringAmountError, setRecurringAmountError] = useState<
    string | null
  >(null);
  const [unmatchEntry, setUnmatchEntry] =
    useState<ElfLoanDetailTransactionEntry | null>(null);
  const [removeChargeEntry, setRemoveChargeEntry] =
    useState<ElfLoanDetailTransactionEntry | null>(null);
  const [correctEntry, setCorrectEntry] =
    useState<ElfLoanDetailTransactionEntry | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(
    HISTORY_PAGE_SIZE_DEFAULT
  );
  const [principalOnlyBusyId, setPrincipalOnlyBusyId] = useState<string | null>(
    null
  );
  const [principalOnlyError, setPrincipalOnlyError] = useState<string | null>(
    null
  );
  const [principalOnlyConfirm, setPrincipalOnlyConfirm] = useState<{
    loan: ElfLoanDetailLoan;
    nextValue: boolean;
  } | null>(null);
  const [quoteDialog, setQuoteDialog] = useState<{
    kind: 'payoff' | 'catch-up';
    loan: ElfLoanDetailLoan;
    loading: boolean;
    error: string | null;
    payoff: LoanPayoffQuote | null;
    catchUp: LoanCatchUpQuote | null;
    payoffAsOf: string;
  } | null>(null);

  const { data, isLoading, isError, refetch } = useApiGet<
    FinanceElfLoanDetailResponse,
    string
  >(
    ['finance-elf-loan-detail', id!],
    getFinanceElfLoanDetail,
    {
      enabled: !!id,
      retry: 1,
    },
    id!
  );

  const paymentValuesDiffer = useMemo(() => {
    if (!data?.loans?.length || data.loans.length < 2) return false;
    const first = data.loans[0];
    return data.loans.slice(1).some((loan) => {
      return (
        loan.auto_payment !== first.auto_payment ||
        (loan.next_payment_date || '') !== (first.next_payment_date || '') ||
        (loan.anniversary_date || '') !== (first.anniversary_date || '')
      );
    });
  }, [data?.loans]);

  const historyRows = useMemo(
    () => data?.transaction_history ?? [],
    [data?.transaction_history]
  );
  const pagedHistory = useMemo(() => {
    const start = historyPage * historyRowsPerPage;
    return historyRows.slice(start, start + historyRowsPerPage);
  }, [historyRows, historyPage, historyRowsPerPage]);

  const toggleExpanded = (entryId: string) => {
    setExpandedEntryIds((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId]
    );
  };

  const applyPrincipalOnly = async (
    loan: ElfLoanDetailLoan,
    nextValue: boolean
  ) => {
    setPrincipalOnlyBusyId(loan.id);
    setPrincipalOnlyError(null);
    try {
      await setLoanPrincipalOnly(loan.id, nextValue);
      setPrincipalOnlyConfirm(null);
      await refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update principal only';
      setPrincipalOnlyError(message);
    } finally {
      setPrincipalOnlyBusyId(null);
    }
  };

  const openRecurringAmountDialog = () => {
    const current = data?.payment_info?.recurring?.amount;
    setRecurringAmountInput(
      current != null && !Number.isNaN(current) ? current.toFixed(2) : ''
    );
    setRecurringAmountError(null);
    setRecurringAmountOpen(true);
  };

  const saveRecurringAmount = async () => {
    if (!id) return;
    const parsed = Number.parseFloat(recurringAmountInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setRecurringAmountError('Enter an amount greater than 0');
      return;
    }
    const cents = Math.round(parsed * 100);
    if (Math.abs(parsed * 100 - cents) > 0.001) {
      setRecurringAmountError('Amount may have at most 2 decimal places');
      return;
    }
    setRecurringAmountBusy(true);
    setRecurringAmountError(null);
    try {
      await patchFinanceElfLoanRecurringAmount(id, cents / 100);
      setRecurringAmountOpen(false);
      setPaymentSuccess('Recurring payment amount updated');
      await refetch();
    } catch (err) {
      setRecurringAmountError(
        getApiErrorMessage(err) || 'Failed to update recurring amount'
      );
    } finally {
      setRecurringAmountBusy(false);
    }
  };

  const onPrincipalOnlyToggle = (loan: ElfLoanDetailLoan, checked: boolean) => {
    if (checked === loan.principal_only) return;
    setPrincipalOnlyConfirm({ loan, nextValue: checked });
  };

  const openPayoffQuote = async (
    loan: ElfLoanDetailLoan,
    asOf: string = todayISODate()
  ) => {
    setQuoteDialog({
      kind: 'payoff',
      loan,
      loading: true,
      error: null,
      payoff: null,
      catchUp: null,
      payoffAsOf: asOf,
    });
    try {
      const payoff = await getLoanPayoffQuote(loan.id, asOf);
      setQuoteDialog({
        kind: 'payoff',
        loan,
        loading: false,
        error: null,
        payoff,
        catchUp: null,
        payoffAsOf: asOf,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load payoff quote';
      setQuoteDialog({
        kind: 'payoff',
        loan,
        loading: false,
        error: message,
        payoff: null,
        catchUp: null,
        payoffAsOf: asOf,
      });
    }
  };

  const openCatchUpQuote = async (loan: ElfLoanDetailLoan) => {
    setQuoteDialog({
      kind: 'catch-up',
      loan,
      loading: true,
      error: null,
      payoff: null,
      catchUp: null,
      payoffAsOf: todayISODate(),
    });
    try {
      const catchUp = await getLoanCatchUpQuote(loan.id);
      setQuoteDialog({
        kind: 'catch-up',
        loan,
        loading: false,
        error: null,
        payoff: null,
        catchUp,
        payoffAsOf: todayISODate(),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load catch-up quote';
      setQuoteDialog({
        kind: 'catch-up',
        loan,
        loading: false,
        error: message,
        payoff: null,
        catchUp: null,
        payoffAsOf: todayISODate(),
      });
    }
  };

  return (
    <Grid container spacing={2.5} px={2} pb={3}>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/finance/elf-loans')}
            sx={{ textTransform: 'none' }}
          >
            Back to ELF Loans
          </Button>
          <Typography variant="h4" color="primary" fontWeight={600}>
            ELF Loan Detail
          </Typography>
          {data?.loan_info?.borrower_name && (
            <Typography variant="body1" color="text.secondary">
              {data.loan_info.borrower_name}
            </Typography>
          )}
        </Stack>
      </Grid>

      {isError && !data && (
        <Grid item xs={12}>
          <Alert
            severity="error"
            action={<Button onClick={() => refetch()}>Retry</Button>}
          >
            Unable to load ELF loan detail.
          </Alert>
        </Grid>
      )}

      {isLoading && !data && (
        <Grid item xs={12}>
          <Typography color="text.secondary">Loading...</Typography>
        </Grid>
      )}

      {paymentSuccess && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={() => setPaymentSuccess(null)}>
            {paymentSuccess}
          </Alert>
        </Grid>
      )}

      {data && (
        <>
          <Grid item xs={12} md={6}>
            <SectionCard title="Loan Info">
              <Stack>
                <DetailField
                  label="Name"
                  value={
                    data.loan_info.borrower_name ? (
                      <Link
                        component={RouterLink}
                        to={`/user/${data.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {data.loan_info.borrower_name}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <DetailField
                  label="iMIS ID"
                  value={data.loan_info.imis_id || '—'}
                />
                <DetailField
                  label="Email"
                  value={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Typography variant="body2" component="span">
                        {data.loan_info.borrower_email || '—'}
                      </Typography>
                      <Tooltip title="Send portal access email (coming soon)">
                        <span>
                          <IconButton
                            size="small"
                            disabled
                            aria-label="Send portal access email"
                          >
                            <MailOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  }
                />
                <DetailField
                  label="Original Loan Amount"
                  value={formatMoney(data.loan_info.original_amount)}
                />
                <DetailField
                  label="Principal Balance"
                  value={formatMoney(data.loan_info.principal_balance)}
                />
                <DetailField
                  label="Interest Balance"
                  value={formatMoney(data.loan_info.interest_balance)}
                />
                <DetailField
                  label="Accrued Balance"
                  value={formatMoney(data.loan_info.accrued_balance)}
                />
                <DetailField
                  label="Interest Rate"
                  value={formatInterestRate(data.loan_info.interest_rate)}
                />
                <DetailField
                  label="Maturity Date"
                  value={formatCalendarDate(data.loan_info.maturity_date)}
                />
                <DetailField
                  label="Currency"
                  value={data.loan_info.currency || '—'}
                />
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Loan Actions
              </Typography>
              {principalOnlyError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {principalOnlyError}
                </Alert>
              )}
              <Stack spacing={0.75}>
                {data.loans.map((loan) => (
                  <Stack
                    key={loan.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {[
                          loan.maturity_date
                            ? `Matures ${formatCalendarDate(loan.maturity_date)}`
                            : null,
                          loan.original_payment_amount != null
                            ? `Installment ${formatBalanceValue(loan.original_payment_amount)}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </Typography>
                      <FormControlLabel
                        sx={{ mt: 0.5, ml: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!loan.principal_only}
                            disabled={principalOnlyBusyId === loan.id}
                            onChange={(e) =>
                              onPrincipalOnlyToggle(loan, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Principal only (do not bill interest)
                          </Typography>
                        }
                      />
                      <Stack
                        direction="row"
                        spacing={1}
                        mt={0.5}
                        flexWrap="wrap"
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void openPayoffQuote(loan)}
                          sx={{ textTransform: 'none' }}
                        >
                          Calculate payoff
                        </Button>
                        {(loan.catch_up_amount ?? 0) > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void openCatchUpQuote(loan)}
                            sx={{ textTransform: 'none' }}
                          >
                            Calculate catch-up
                          </Button>
                        )}
                      </Stack>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => setCosignerLoan(loan)}
                      sx={{ textTransform: 'none' }}
                    >
                      Co-signers
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Payment Info"
              action={
                paymentValuesDiffer ? (
                  <Chip
                    label="Values differ by loan"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ) : undefined
              }
            >
              <Stack mb={2}>
                <DetailField
                  label="Payment Status"
                  value={
                    <Chip
                      size="small"
                      label={
                        data.payment_info.payment_status === 'late'
                          ? data.payment_info.days_late
                            ? `Late · ${data.payment_info.days_late} days`
                            : 'Late'
                          : 'On-Time'
                      }
                      color={
                        data.payment_info.payment_status === 'late'
                          ? 'warning'
                          : 'success'
                      }
                      variant="outlined"
                    />
                  }
                />
                <DetailField
                  label="Schedule Status"
                  value={
                    <Chip
                      size="small"
                      label={
                        data.payment_info.schedule_status === 'behind'
                          ? (data.payment_info.catch_up_total ?? 0) > 0
                            ? `Behind · ${formatMoney(data.payment_info.catch_up_total ?? 0)}`
                            : 'Behind'
                          : 'On Schedule'
                      }
                      color={
                        data.payment_info.schedule_status === 'behind'
                          ? 'warning'
                          : 'success'
                      }
                      variant="outlined"
                    />
                  }
                />
                <DetailField
                  label="Last Payment Type"
                  value={data.payment_info.payment_type || '—'}
                />
                <DetailField
                  label="Last Payment Date"
                  value={
                    data.payment_info.payment_date
                      ? formatCalendarDate(data.payment_info.payment_date)
                      : '—'
                  }
                />
                <DetailField
                  label="Next Payment Due"
                  value={formatCalendarDate(data.payment_info.next_payment_due)}
                />
                <DetailField
                  label="Next Interest Billing Date"
                  value={formatCalendarDate(
                    data.payment_info.next_interest_billing_date
                  )}
                />
                <DetailField
                  label="Regular Payment Amount"
                  value={
                    data.payment_info.next_expected_receivable_amount || '—'
                  }
                />
                {data.payment_info.recurring && (
                  <>
                    <Box sx={{ pt: 1, pb: 0.5 }}>
                      <Chip
                        size="small"
                        label="Recurring active"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                    <DetailField
                      label="Recurring Amount"
                      value={
                        data.payment_info.recurring.amount != null
                          ? formatMoney(data.payment_info.recurring.amount)
                          : '—'
                      }
                    />
                    <DetailField
                      label="Account Ending"
                      value={
                        data.payment_info.recurring.account_last4
                          ? `•••• ${data.payment_info.recurring.account_last4}`
                          : '—'
                      }
                    />
                    <DetailField
                      label="Expiration"
                      value={formatRecurringExpDate(
                        data.payment_info.recurring.exp_date
                      )}
                    />
                    <DetailField
                      label="Billing Day"
                      value={
                        data.payment_info.recurring.day_of_month != null
                          ? String(data.payment_info.recurring.day_of_month)
                          : '—'
                      }
                    />
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  onClick={() => setPaymentDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Make Payment
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setAddChargeOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Add Charge
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setAccountOnFileOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Add account on file
                </Button>
                {data.payment_info.recurring && (
                  <Button
                    variant="outlined"
                    onClick={openRecurringAmountDialog}
                    sx={{ textTransform: 'none' }}
                  >
                    Update Recurring Amount
                  </Button>
                )}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Transaction History">
              {!historyRows.length ? (
                <Typography color="text.secondary">
                  No transaction history found.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            '& th': {
                              fontWeight: 600,
                              bgcolor: 'grey.50',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        >
                          <TableCell>Loan #</TableCell>
                          <TableCell>Event</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Interest</TableCell>
                          <TableCell align="right">Principal</TableCell>
                          <TableCell align="right">Fees</TableCell>
                          <TableCell align="right">Interest Bal</TableCell>
                          <TableCell align="right">
                            Accrued Interest Bal
                          </TableCell>
                          <TableCell align="right">Principal Bal</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedHistory.map((entry) => {
                          const expanded = expandedEntryIds.includes(entry.id);
                          const detailLines = transactionExpandLines(entry);
                          const applied = appliedByBucket(entry);
                          return (
                            <Fragment key={entry.id}>
                              <TableRow
                                hover
                                sx={{
                                  cursor: detailLines.length
                                    ? 'pointer'
                                    : 'default',
                                  '& td': { py: 1.25 },
                                }}
                                onClick={() => {
                                  if (detailLines.length) {
                                    toggleExpanded(entry.id);
                                  }
                                }}
                              >
                                <TableCell>
                                  {entry.loan_number || entry.loan_id}
                                </TableCell>
                                <TableCell>
                                  {entry.paypal_settlement_id ? (
                                    <Link
                                      component={RouterLink}
                                      to={`/finance/elf-payments/paypal/${entry.paypal_settlement_id}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {formatPaymentEventLabel(entry)}
                                    </Link>
                                  ) : (
                                    formatPaymentEventLabel(entry)
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatCalendarDate(entry.event_date)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(entry.amount)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatApplied(applied.interest)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatApplied(applied.principal)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatApplied(applied.fees)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatBalanceValue(
                                    entry.new_interest_balance
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {entry.new_accrued_balance == null
                                    ? '—'
                                    : formatBalanceValue(
                                        entry.new_accrued_balance
                                      )}
                                </TableCell>
                                <TableCell align="right">
                                  {formatBalanceValue(
                                    entry.new_principal_balance
                                  )}
                                </TableCell>
                                <TableCell>
                                  {entry.event_type === 'Payment' &&
                                  entry.payment_id &&
                                  entry.payment_method === 'check' ? (
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCorrectEntry(entry);
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Correct
                                    </Button>
                                  ) : entry.event_type === 'Payment' &&
                                    entry.payment_id &&
                                    (entry.payment_method === 'paypal' ||
                                      entry.payment_method === 'ach' ||
                                      entry.paypal_settlement_id) ? (
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setUnmatchEntry(entry);
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Unmatch
                                    </Button>
                                  ) : entry.event_type === 'Charge' &&
                                    entry.charge_id ? (
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRemoveChargeEntry(entry);
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Remove
                                    </Button>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                              </TableRow>
                              {expanded && detailLines.length > 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={11}
                                    sx={{ py: 1.25, pl: 4, bgcolor: 'grey.50' }}
                                  >
                                    <Stack spacing={0.5}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={600}
                                      >
                                        Balance changes
                                      </Typography>
                                      {detailLines.map((line) => (
                                        <Typography
                                          key={line}
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {line}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={historyRows.length}
                    page={historyPage}
                    onPageChange={(_e, newPage) => setHistoryPage(newPage)}
                    rowsPerPage={historyRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setHistoryRowsPerPage(parseInt(e.target.value, 10));
                      setHistoryPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                  />
                </>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Contact History">
              {data.contact_history.length ? (
                <Stack spacing={1.25} divider={<Divider />}>
                  {data.contact_history.map((entry) => (
                    <Box key={entry.id}>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.channel} to {entry.recipient} on{' '}
                        {formatTimestamp(entry.sent_at)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  No contact history available in pass 1.
                </Typography>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Recent PayPal Activity">
              {(data.recent_paypal_activity ?? []).length ? (
                <Stack spacing={1.25} divider={<Divider />}>
                  {(data.recent_paypal_activity ?? []).map((entry) => {
                    const when =
                      entry.transaction_time != null
                        ? formatTimestamp(entry.transaction_time)
                        : entry.report_date
                          ? formatCalendarDate(entry.report_date)
                          : '—';
                    const response =
                      entry.response_msg ||
                      entry.result_code_msg ||
                      entry.status ||
                      '—';
                    return (
                      <Box key={entry.id}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {formatMoney(entry.amount)}
                            {entry.currency_code
                              ? ` ${entry.currency_code}`
                              : ''}
                          </Typography>
                          {entry.has_error && (
                            <Chip size="small" label="Error" color="error" />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {entry.paypal_transaction_id} · {when}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {response}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  No recent PayPal activity.
                </Typography>
              )}
            </SectionCard>
          </Grid>
        </>
      )}

      {data && (
        <>
          <ElfLoanPaymentDialog
            open={paymentDialogOpen}
            onClose={() => setPaymentDialogOpen(false)}
            borrowerUserId={data.id}
            currency={data.loan_info.currency}
            onSuccess={() => {
              setPaymentSuccess('Payment applied successfully.');
              refetch();
            }}
          />
          <ElfLoanAccountOnFileDialog
            open={accountOnFileOpen}
            onClose={() => setAccountOnFileOpen(false)}
            borrowerUserId={data.id}
            borrowerName={data.loan_info.borrower_name}
            defaultAmount={(() => {
              const raw =
                data.payment_info.next_expected_receivable_amount?.replace(
                  /[^0-9.]/g,
                  ''
                );
              const n = raw ? Number.parseFloat(raw) : NaN;
              return Number.isFinite(n) && n > 0 ? n : null;
            })()}
            onSuccess={() => {
              setPaymentSuccess('Recurring card on file enrolled.');
              refetch();
            }}
          />
          <Dialog
            open={recurringAmountOpen}
            onClose={() =>
              !recurringAmountBusy && setRecurringAmountOpen(false)
            }
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Update Recurring Amount</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Changes the amount billed on the active PayPal recurring
                profile. Card details and billing day are not changed.
              </DialogContentText>
              {recurringAmountError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {recurringAmountError}
                </Alert>
              )}
              <TextField
                autoFocus
                label="Recurring amount"
                type="number"
                fullWidth
                size="small"
                value={recurringAmountInput}
                onChange={(e) => setRecurringAmountInput(e.target.value)}
                inputProps={{ min: 0.01, step: '0.01' }}
                disabled={recurringAmountBusy}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setRecurringAmountOpen(false)}
                disabled={recurringAmountBusy}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveRecurringAmount}
                disabled={recurringAmountBusy}
                sx={{ textTransform: 'none' }}
              >
                {recurringAmountBusy ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Dialog
        open={!!quoteDialog}
        onClose={() => setQuoteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {quoteDialog?.kind === 'catch-up'
            ? 'Catch-up amount'
            : 'Payoff amount'}
        </DialogTitle>
        <DialogContent dividers>
          {quoteDialog?.kind === 'payoff' && (
            <Stack spacing={1.5} mb={2}>
              <TextField
                label="Payoff as of"
                type="date"
                size="small"
                value={quoteDialog.payoffAsOf}
                onChange={(e) => {
                  const next = e.target.value;
                  setQuoteDialog((prev) =>
                    prev ? { ...prev, payoffAsOf: next } : prev
                  );
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ maxWidth: 220 }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={quoteDialog.loading || !quoteDialog.payoffAsOf.trim()}
                onClick={() =>
                  void openPayoffQuote(quoteDialog.loan, quoteDialog.payoffAsOf)
                }
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Recalculate
              </Button>
            </Stack>
          )}
          {quoteDialog?.loading && (
            <Typography color="text.secondary">Calculating…</Typography>
          )}
          {quoteDialog?.error && (
            <Alert severity="error">{quoteDialog.error}</Alert>
          )}
          {!quoteDialog?.loading &&
            !quoteDialog?.error &&
            quoteDialog?.kind === 'payoff' &&
            quoteDialog.payoff && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  As of {formatCalendarDate(quoteDialog.payoff.as_of)}. Billed
                  balances plus accrued interest not yet billed through that
                  date. Quote only — does not bill deferred. Recheck if payment
                  is delayed.
                </Typography>
                <DetailField
                  label="Principal"
                  value={formatMoney(quoteDialog.payoff.principal)}
                />
                <DetailField
                  label="Interest due"
                  value={formatMoney(quoteDialog.payoff.interest)}
                />
                <DetailField
                  label="Charges"
                  value={formatMoney(quoteDialog.payoff.charges)}
                />
                <DetailField
                  label="Accrued (unbilled)"
                  value={formatMoney(quoteDialog.payoff.accrued_unbilled)}
                />
                <Divider />
                <DetailField
                  label="Total payoff"
                  value={formatMoney(quoteDialog.payoff.total)}
                />
              </Stack>
            )}
          {!quoteDialog?.loading &&
            !quoteDialog?.error &&
            quoteDialog?.kind === 'catch-up' &&
            quoteDialog.catchUp && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  As of {formatCalendarDate(quoteDialog.catchUp.as_of)}.
                  Principal schedule catch-up only. Recheck if payment is
                  delayed.
                </Typography>
                <DetailField
                  label="Catch-up amount"
                  value={formatMoney(quoteDialog.catchUp.catch_up_amount)}
                />
                <DetailField
                  label="Contractual installment"
                  value={formatMoney(
                    quoteDialog.catchUp.original_payment_amount
                  )}
                />
                <DetailField
                  label="Required monthly (to finish on time)"
                  value={formatMoney(quoteDialog.catchUp.required_monthly)}
                />
                <DetailField
                  label="Months remaining"
                  value={String(quoteDialog.catchUp.months_remaining)}
                />
              </Stack>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!principalOnlyConfirm}
        onClose={() => setPrincipalOnlyConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {principalOnlyConfirm?.nextValue
            ? 'Enable principal only?'
            : 'Disable principal only?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {principalOnlyConfirm?.nextValue
              ? 'Interest will keep accruing as deferred, but anniversary billing will be skipped until this is turned off. Existing interest due is not reversed.'
              : `Bill ${formatMoney(
                  principalOnlyConfirm?.loan.current_deferred_balance ?? 0
                )} deferred interest as due now? This cannot be undone automatically.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrincipalOnlyConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !!principalOnlyConfirm &&
              principalOnlyBusyId === principalOnlyConfirm.loan.id
            }
            onClick={() => {
              if (principalOnlyConfirm) {
                void applyPrincipalOnly(
                  principalOnlyConfirm.loan,
                  principalOnlyConfirm.nextValue
                );
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AddChargeDialog
        open={addChargeOpen}
        loans={(data?.loans ?? []).map((loan) => ({
          id: loan.id,
          loan_number: loan.loan_number,
        }))}
        onClose={() => setAddChargeOpen(false)}
        onDone={() => {
          void refetch();
        }}
      />
      <RemoveChargeDialog
        entry={removeChargeEntry}
        onClose={() => setRemoveChargeEntry(null)}
        onDone={() => {
          void refetch();
        }}
      />
      <UnmatchPayPalPaymentDialog
        entry={unmatchEntry}
        onClose={() => setUnmatchEntry(null)}
        onDone={() => {
          void refetch();
        }}
      />
      <CorrectCheckPaymentDialog
        entry={correctEntry}
        borrowerId={id || ''}
        borrowerName={data?.loan_info?.borrower_name || ''}
        onClose={() => setCorrectEntry(null)}
        onDone={() => {
          void refetch();
        }}
      />

      <Dialog
        open={!!cosignerLoan}
        onClose={() => setCosignerLoan(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Co-signers</DialogTitle>
        <DialogContent dividers>
          {!cosignerLoan?.cosigners?.length ? (
            <Typography color="text.secondary">
              No co-signers on file for this installment.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {cosignerLoan.cosigners.map((c) => {
                const name = [c.first_name, c.last_name]
                  .filter(Boolean)
                  .join(' ')
                  .trim();
                const address = formatCosignerAddress(c);
                return (
                  <Box key={c.id}>
                    <Typography variant="caption" color="text.secondary">
                      {c.contact_type}
                    </Typography>
                    <Typography fontWeight={600}>{name || '—'}</Typography>
                    {c.relationship ? (
                      <Typography variant="body2" color="text.secondary">
                        {c.relationship}
                      </Typography>
                    ) : null}
                    {c.email ? (
                      <Typography variant="body2" color="text.secondary">
                        {c.email}
                      </Typography>
                    ) : null}
                    {c.phone_number ? (
                      <Typography variant="body2" color="text.secondary">
                        {c.phone_number}
                      </Typography>
                    ) : null}
                    {address ? (
                      <Typography variant="body2" color="text.secondary">
                        {address}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCosignerLoan(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ElfLoanDetail;
